import { useCallback, useMemo, useState } from 'react';

import { apiService } from '../../services';
import type {
  DashboardPaymentInvoiceTypeGraphicResponse,
  RelatorioDownloadRetorno,
  DashboardPaymentSummaryItem,
  DashboardPaymentSummaryItemDTO,
  DashboardPaymentSummaryRequestParams,
  DashboardPaymentGraphicRequestParams,
} from '../../types';

export interface UseVidaDashboardPaymentParams {
  tpVisao: string;
  idCorretor?: number;
  idEstipulante?: number;
  apoliceSubList: Array<{ nroApolice: string; cdSubEstipulante: string }>;
}


const mapSummaryItem = (item: DashboardPaymentSummaryItemDTO): DashboardPaymentSummaryItem => {
  // Map from API response fields to component fields
  const id = item.idContrato ?? item.id ?? 0;
  const name = item.nmEstipulante ?? item.name ?? '-';
  const percentPending = item.vlrPercPendente ?? item.percentPending ?? 0;
  const percentIssued = item.vlrPercEmitido ?? item.percentIssued ?? 0;
  const valuePending = item.vlrPendente ?? item.valuePending ?? 0;
  const valueIssued = item.vlrEmitido ?? item.valueIssued ?? 0;
  const status = item.statusPagamento ?? item.status ?? '-';
  const contractNumber = item.nroApolice ?? item.contractNumber;

  return {
    id,
    name,
    percentPending,
    percentIssued,
    valuePending,
    valueIssued,
    status,
    contractNumber,
  };
};

export const useVidaDashboardPayment = (params?: UseVidaDashboardPaymentParams) => {
  const [invoices, setInvoices] = useState<DashboardPaymentInvoiceTypeGraphicResponse | null>(null);
  const [relatorioDownloadRetorno, setRelatorioDownloadRetorno] = useState<RelatorioDownloadRetorno | null>(null);
  const [summaryTableData, setSummaryTableData] = useState<DashboardPaymentSummaryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(6);

  const runWithLoader = useCallback(async <T,>(fn: () => Promise<T>): Promise<T> => {
    setIsLoading(true);
    try {
      return await fn();
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Get dashboard payment invoices (graphic data) + zip report for export
  const getDashboardPaymentInvoiceTypeGraphic = useCallback(async (
    params: DashboardPaymentGraphicRequestParams
  ) => {
    return runWithLoader(async () => {
      const result = await apiService.dashboardPayment.getDashboardPaymentInvoiceTypeGraphic(params);
      const graphicData = result?.graphic ?? null;
      setInvoices(graphicData);
      setRelatorioDownloadRetorno(result?.relatorioDownloadRetorno ?? null);
      return result;
    });
  }, [runWithLoader]);

  // Get dashboard payment summary data by view type (with pagination)
  const getDashboardPaymentSummary = useCallback(async (
    requestParams: DashboardPaymentSummaryRequestParams
  ): Promise<DashboardPaymentSummaryItem[]> => {
    return runWithLoader(async () => {
      const response = await apiService.dashboardPayment.getDashboardPaymentSummary({
        ...requestParams,
        page: requestParams.page ?? 0,
        size: requestParams.size ?? 6,
      });

      // Extract pagina from response (API returns { mensagem, pagina: { content, pageNumber, ... } })
      const pagina = response.pagina || response;
      const items = pagina.content.map(mapSummaryItem);
      setSummaryTableData(items);
      setCurrentPage(pagina.pageNumber);
      setTotalPages(pagina.totalPages);
      setTotalElements(pagina.totalElements);
      setPageSize(pagina.pageSize);

      return items;
    });
  }, [runWithLoader]);

  // Load summary (for initial load or pagination) – request stays in hook
  const loadSummaryData = useCallback(async (filters?: { page?: number }) => {
    if (!params?.tpVisao) return;
    return getDashboardPaymentSummary({
      tpVisao: params.tpVisao,
      idCorretor: params.idCorretor,
      idEstipulante: params.idEstipulante,
      apoliceSubList: params.apoliceSubList ?? [],
      page: filters?.page ?? 0,
      size: 6,
    });
  }, [params?.tpVisao, params?.idCorretor, params?.idEstipulante, params?.apoliceSubList, getDashboardPaymentSummary]);

  // Initial load (chart + summary): DashboardPaymentTab uses runTabMountSequence.

  // Calculate total web and manual amounts from invoices data
  const totalWebAmount = useMemo(() => {
    if (!invoices) return 0;
    return invoices.valorWeb || 0;
  }, [invoices]);

  const totalManualAmount = useMemo(() => {
    if (!invoices) return 0;
    return invoices.valorManual || 0;
  }, [invoices]);

  // Prepare chart data for Recharts (simple web vs manual comparison)
  // Chart shows percentages (vlrPercFatWeb and vlrPercFatManual) as bars
  const chartData = useMemo(() => {
    if (!invoices) return [];

    const webPercentage = invoices.vlrPercFatWeb || 0;
    const manualPercentage = invoices.vlrPercFatManual || 0;
    const webValor = invoices.valorWeb || 0;
    const manualValor = invoices.valorManual || 0;

    return [
      {
        name: 'Meio de Faturamento',
        Web: webPercentage,
        Manual: manualPercentage,
        webValor: webValor,
        manualValor: manualValor,
        webPercentage: webPercentage,
        manualPercentage: manualPercentage,
        webCount: invoices.qtdFatWeb || 0,
        manualCount: invoices.qtdFatManual || 0,
      },
    ];
  }, [invoices]);

  // Format currency helper
  const formatCurrency = useCallback((value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  }, []);

  // Custom Y-axis formatter
  const formatYAxis = useCallback((value: number) => {
    if (value >= 1000) {
      return `R$ ${(value / 1000).toFixed(0)}k`;
    }
    return `R$ ${value}`;
  }, []);

  // Format percent helper
  const formatPercent = useCallback((value: number) => {
    return `${value.toFixed(2)}%`;
  }, []);

  return {
    invoices,
    relatorioDownloadRetorno,
    summaryTableData,
    isLoading,
    totalWebAmount,
    totalManualAmount,
    chartData,
    formatCurrency,
    formatYAxis,
    formatPercent,
    getDashboardPaymentInvoiceTypeGraphic,
    getDashboardPaymentSummary,
    loadSummaryData,
    // Pagination state
    currentPage,
    totalPages,
    totalElements,
    pageSize,
  };
};
